import { useMemo, useState } from 'react';
import { Icon } from '../../../../../../base/components/Icon';
import { PopupMenu, PopupMenuOption } from '../../../../../../base/components/PopupMenu';
import { useAppDispatch } from '../../../../../../base/hooks';
import { __t } from '../../../../../../base/utils/t';
import { VacancyData, WorkerData } from '../../../api/interfaces';
import { HireStatus } from '../../../enums';
import { responsesActions } from '../../../redux/slices/responsesSlice';
import { InviteWorkerButton } from '../InviteWorkerButton';
import { ResponseHistoryModal } from '../ResponseHistoryModal';
import { StatusSelect } from '../StatusSelect';

import './styles.scss';

interface Props {
    responseId: number;
    isResponseArchived: boolean;
    hireStatus: HireStatus;
    workerData: WorkerData;
    vacancyData: VacancyData;
    showInviteButton: boolean;
    isQuickResponse: boolean;
    markNewResponseAsReadHandler: () => void;
}

export const Interactions = ({
    responseId,
    isResponseArchived,
    hireStatus,
    vacancyData,
    workerData,
    showInviteButton,
    isQuickResponse,
    markNewResponseAsReadHandler,
}: Props) => {
    const dispatch = useAppDispatch();

    const [showReponseHistoryModal, setShowReponseHistoryModal] = useState(false);

    const menuOptions: PopupMenuOption[] = useMemo(() => {
        const result: PopupMenuOption[] = [];

        if (!isQuickResponse) {
            result.push({
                icon: 'user-helmet-safety-light',
                text: __t('Дивитися анкету'),
                onClick: () => window.open(workerData.url, '_blank', 'noreferrer'),
            });
            result.push({
                icon: 'clock-rotate-left-light',
                text: __t('Історія відгуків'),
                onClick: () => setShowReponseHistoryModal(true),
            });
        }

        if (isResponseArchived) {
            result.push({
                icon: 'arrow-turn-down-right-light',
                text: __t('Розархівувати'),
                onClick: () => dispatch(responsesActions.setResponseArchivationStatusRequest(responseId, false)),
                itemClassName: 'responses-table-interactions__unarchive-menu-item',
            });
        } else {
            result.push({
                icon: 'box-archive-light',
                text: __t('В архів'),
                onClick: () => dispatch(responsesActions.setResponseArchivationStatusRequest(responseId, true)),
                itemClassName: 'responses-table-interactions__archive-menu-item',
            });
        }

        return result;
    }, [isResponseArchived, isQuickResponse]);

    const content = useMemo(() => {
        if (showInviteButton) {
            return (
                <div className='responses-table-interactions__button-wrap'>
                    <InviteWorkerButton
                        isVacancyActive={vacancyData.isActive}
                        vacancyId={vacancyData.id}
                        workerId={workerData.id}
                        markNewResponseAsReadHandler={markNewResponseAsReadHandler}
                    />
                </div>
            );
        }

        return (
            <div className='responses-table-interactions__button-wrap'>
                <StatusSelect
                    responseId={responseId}
                    hireStatus={hireStatus}
                    markNewResponseAsReadHandler={markNewResponseAsReadHandler}
                />
            </div>
        );
    }, [hireStatus, responseId, vacancyData, showInviteButton]);

    if (workerData.isDeleted) {
        return null;
    }

    return (
        <div className='responses-table-interactions'>
            {content}
            <div className='responses-table-interactions__dropdown-wrap' onClick={markNewResponseAsReadHandler}>
                <PopupMenu options={menuOptions} align='right' className='responses-table-interactions__dropdown-menu'>
                    <Icon
                        icon='ellipsis-regular'
                        size='20'
                        color='grey'
                        className='responses-table-interactions__dropdown-menu-icon'
                    />
                </PopupMenu>
            </div>
            {showReponseHistoryModal && (
                <ResponseHistoryModal
                    workerData={workerData}
                    isQuickResponse={isQuickResponse}
                    visibility={showReponseHistoryModal}
                    closeModal={() => setShowReponseHistoryModal(false)}
                    markNewResponseAsReadHandler={markNewResponseAsReadHandler}
                />
            )}
        </div>
    );
};
